import { Box, ButtonBase, IconButton, Link, Tooltip, Typography } from '@mui/material'
import InsertChartIcon from '@mui/icons-material/InsertChartOutlined'
import WarningAmberIcon from '@mui/icons-material/WarningAmberRounded'
import { palette } from '../../ui/theme/tokens.js'
import { Sparkline } from '../../ui/charts/Sparkline.js'
import { Variation } from './briques.js'

/**
 * La carte VEDETTE : le chiffre d'affaires de la période, en grand.
 *
 * ⭐ Un seul chiffre dominant par écran — celui qu'on vient chercher en
 * ouvrant l'application. Dessous, ce qu'il en reste (le bénéfice net) et ce
 * qui est sorti (les dépenses), côte à côte : un chiffre d'affaires seul ne
 * dit pas si la boutique gagne sa vie.
 *
 * ⚠️ Texte BLANC sur le bleu de la marque, jamais une couleur de série : le
 * contraste reste lisible, et l'orange du bénéfice sur ce fond ne le serait
 * pas.
 */
export function CarteVedette({
  periode,
  ca,
  variation,
  libelleVariation,
  benefice,
  beneficeNegatif,
  depenses,
  lignesSansPrixAchat,
  tendance,
  onCompleterPrixAchat,
  onClick,
  onGraphe,
}: {
  /** « Aujourd'hui », « Ce mois-ci », « Cette année ». */
  periode: string
  ca: string
  variation: number | null
  libelleVariation?: string
  /** Formaté, ou « — » quand les dépenses manquent (hors ligne). */
  benefice: string
  beneficeNegatif: boolean
  depenses: string
  lignesSansPrixAchat: number
  /** Le chiffre d'affaires, tranche par tranche — la tendance, en blanc. */
  tendance: readonly (number | null)[]
  onCompleterPrixAchat: () => void
  onClick: () => void
  onGraphe: () => void
}): JSX.Element {
  return (
    <Box
      component="section"
      aria-label={`Chiffre d'affaires — ${periode}`}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 2,
        p: { xs: 4, md: 5 },
        color: '#FFFFFF',
        minHeight: 260,
        height: '100%',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        background: `linear-gradient(135deg, ${palette.brand[500]} 0%, ${palette.brand[700]} 100%)`,
        boxShadow: '0 12px 32px rgba(47,107,255,0.28)',
        // Deux disques translucides : du relief, sans image à télécharger.
        '&::before, &::after': {
          content: '""',
          position: 'absolute',
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.08)',
          pointerEvents: 'none',
        },
        '&::before': { width: 220, height: 220, top: -80, right: -60 },
        '&::after': { width: 140, height: 140, bottom: -60, right: 60 },
        '&:has(.vedette-cible:focus-visible)': { outline: 2, outlineColor: palette.brand[200] },
      }}
    >
      {/* Toute la carte mène aux ventes ; le bouton graphe reste un FRÈRE. */}
      <ButtonBase
        className="vedette-cible"
        onClick={onClick}
        aria-label={`Chiffre d'affaires ${periode} : ${ca}. Voir les ventes`}
        sx={{ position: 'absolute', inset: 0, zIndex: 1 }}
      />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, position: 'relative' }}>
        <Typography sx={{ fontSize: 14, fontWeight: 600, opacity: 0.9, flex: 1 }}>
          Chiffre d’affaires · {periode}
        </Typography>
        <Tooltip title="Voir le graphe">
          <IconButton
            size="small"
            aria-label="Voir le graphe : chiffre d'affaires"
            onClick={onGraphe}
            sx={{
              color: '#FFFFFF',
              zIndex: 2,
              bgcolor: 'rgba(255,255,255,0.12)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.22)' },
            }}
          >
            <InsertChartIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Typography
        sx={{
          position: 'relative',
          fontSize: { xs: 32, md: 38 },
          lineHeight: 1.15,
          fontWeight: 800,
          mt: 2,
          letterSpacing: '-0.02em',
          wordBreak: 'break-word',
        }}
      >
        {ca}
      </Typography>
      <Box sx={{ position: 'relative', mt: 1.5, minHeight: 24 }}>
        <Variation
          valeur={variation}
          surFondFonce
          {...(libelleVariation ? { libelle: libelleVariation } : {})}
        />
      </Box>

      {/* La tendance, en blanc : la forme de la période, sans axe ni valeur —
          le détail est dans la courbe d'à côté. */}
      <Box
        sx={{
          position: 'relative',
          flex: 1,
          minHeight: 48,
          display: 'flex',
          alignItems: 'flex-end',
          mt: 3,
        }}
      >
        <Sparkline
          valeurs={tendance}
          titre="Tendance du chiffre d'affaires"
          couleur="#FFFFFF"
          hauteur={56}
        />
      </Box>

      {/* Ce qu'il en reste, et ce qui est sorti — séparés d'un filet. */}
      <Box
        sx={{
          position: 'relative',
          display: 'grid',
          gridTemplateColumns: '1fr 1px 1fr',
          gap: 3,
          mt: 4,
          pt: 3,
          borderTop: '1px solid rgba(255,255,255,0.22)',
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 12, opacity: 0.8 }}>Bénéfice net</Typography>
          <Typography sx={{ fontSize: 18, fontWeight: 700 }} noWrap title={benefice}>
            {beneficeNegatif ? '▼ ' : ''}
            {benefice}
          </Typography>
        </Box>
        <Box sx={{ bgcolor: 'rgba(255,255,255,0.22)' }} />
        <Box sx={{ minWidth: 0 }}>
          {/* Les COURANTES : ce sont elles qui entrent dans le bénéfice. La
              marchandise achetée, elle, n'y entre qu'une fois vendue. */}
          <Typography sx={{ fontSize: 12, opacity: 0.8 }}>Dépenses courantes</Typography>
          <Typography sx={{ fontSize: 18, fontWeight: 700 }} noWrap title={depenses}>
            {depenses}
          </Typography>
        </Box>
      </Box>

      {lignesSansPrixAchat > 0 ? (
        <Link
          component="button"
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            onCompleterPrixAchat()
          }}
          sx={{
            position: 'relative',
            zIndex: 2,
            mt: 2,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            color: '#FFFFFF',
            fontSize: 12,
            textAlign: 'left',
            textDecorationColor: 'rgba(255,255,255,0.6)',
          }}
        >
          <WarningAmberIcon sx={{ fontSize: 15 }} />
          Prix d’achat manquant sur {lignesSansPrixAchat} ligne{lignesSansPrixAchat > 1 ? 's' : ''}{' '}
          : bénéfice surestimé. Compléter →
        </Link>
      ) : null}
    </Box>
  )
}
